// app/frontend/components/layouts/GuestLayout.tsx
import React from "react";
import {
  AppShell,
  Group,
  Box,
  Text,
  Anchor,
  Button,
  Stack,
  Divider,
  Burger,
} from "@mantine/core";
import { Link } from "@inertiajs/react";
import { NavigationDrawer } from "../ui/NavigationDrawer";
import {
  IconVideo,
  IconBook2,
  IconNews,
  IconInfoCircle,
  IconCurrencyDollar,
  IconHelpCircle,
} from "@tabler/icons-react";
import { Footer } from "../ui/Footer/Footer";
import { MenuNavItem } from "../ui/MenuNavItem";
import { useScrollHeader } from "../../hooks/useScrollHeader";
import { HeaderVisibilityProvider } from "../../context/HeaderVisibilityContext";

/**
 * GuestLayout
 * - Header: text logo + [Login] [Start Free Trial]
 * - Compact header on mobile with reduced spacing
 * - No sidebar
 * - Marketing footer (simple): navigation + social
 *   • Desktop: multiple Stacks side-by-side
 *   • Mobile: simplified single vertical Stack
 * - Access to: Free Videos, Dictionary, Posts, About, Pricing
 */

type LayoutProps = {
  children: React.ReactNode;
  title?: string;
  /**
   * Enable scroll-away header behavior on mobile
   * Header will hide when scrolling down and show when scrolling up
   */
  hideOnScroll?: boolean;
};

export default function GuestLayout({
  children,
  hideOnScroll = false,
}: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { headerVisible } = useScrollHeader({ enabled: hideOnScroll });

  // Debug logging
  React.useEffect(() => {
    if (hideOnScroll) {
      console.log("[GuestLayout] hideOnScroll:", hideOnScroll);
      console.log("[GuestLayout] headerVisible:", headerVisible);
      console.log("[GuestLayout] window.innerWidth:", window.innerWidth);
    }
  }, [hideOnScroll, headerVisible]);

  const appShellStyles = React.useMemo(
    () => ({
      main: {
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
        minHeight: "100vh",
        color: "#F0F0F0",
        paddingTop: "clamp(1rem, 4vw, 3rem)",
        paddingBottom: "24px", // Ensure space for footer
      } as React.CSSProperties,
      header: {
        background: "rgba(0, 0, 0, 0.4)",
        borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
        backdropFilter: "blur(10px)",
        ...(hideOnScroll && {
          position: "sticky" as const,
          top: 0,
          zIndex: 100,
        }),
        transition: hideOnScroll
          ? "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)"
          : "all 0.3s ease",
        transform: hideOnScroll
          ? headerVisible
            ? "translateY(0)"
            : "translateY(-100%)"
          : "translateY(0)",
        opacity: hideOnScroll ? (headerVisible ? 1 : 0) : 1,
        pointerEvents:
          hideOnScroll && !headerVisible
            ? ("none" as const)
            : ("auto" as const),
        willChange: hideOnScroll ? "transform, opacity" : undefined,
      } as React.CSSProperties,
      footer: {
        background: "rgba(0, 0, 0, 0.4)",
        borderTop: "1px solid rgba(255, 152, 0, 0.3)",
        backdropFilter: "blur(10px)",
        position: "relative" as const,
        zIndex: 10,
      } as React.CSSProperties,
    }),
    [hideOnScroll, headerVisible]
  );

  return (
    <AppShell
      header={{
        height: { base: 56, sm: 60 },
      }}
      footer={{
        height: { base: "auto", sm: "auto" },
      }}
      padding={{ base: "xs", sm: "md" }}
      styles={{
        main: appShellStyles.main,
        header: appShellStyles.header,
        footer: appShellStyles.footer,
      }}
    >
      {/* Header - Compact on mobile */}
      <AppShell.Header style={appShellStyles.header}>
        <Group
          h="100%"
          px={{ base: "sm", sm: "md" }}
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap="sm">
            {/* Hamburger menu - mobile only */}
            <Burger
              opened={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((v) => !v)}
              size="sm"
              color="#FF9800"
              hiddenFrom="md"
            />
            <Anchor
              component={Link}
              href="/"
              underline="never"
              style={{
                textDecoration: "none",
              }}
            >
              <Text
                fw={700}
                size="lg"
                style={{
                  letterSpacing: 0.3,
                  fontSize: "clamp(0.875rem, 3.5vw, 1.125rem)", // Smaller on mobile
                  whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, #FFC107, #FF9800)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  cursor: "pointer",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {/* Show abbreviated version on mobile, full on desktop */}
                <Box component="span" hiddenFrom="sm">
                  L2K 2.0
                </Box>
                <Box component="span" visibleFrom="sm">
                  Learntokiz 2.0
                </Box>
              </Text>
            </Anchor>
          </Group>

          {/* Desktop auth buttons */}
          <Group gap="xs" wrap="nowrap" visibleFrom="md">
            <Button
              component={Link}
              href="/login"
              variant="subtle"
              c="#FF9800"
              size="sm"
              styles={{
                root: {
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                },
              }}
            >
              Login
            </Button>
            <Button
              disabled
              variant="filled"
              size="sm"
              title="Sign up opens at 2.0 launch"
              styles={{
                root: {
                  background: "rgba(255, 152, 0, 0.15)",
                  border: "1px solid rgba(255, 152, 0, 0.25)",
                  color: "rgba(255, 152, 0, 0.6)",
                  fontWeight: 600,
                  cursor: "not-allowed",
                },
              }}
            >
              Sign Up · Coming soon
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Menu Drawer - visible on all screen sizes */}
      <NavigationDrawer
        opened={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        title="Menu"
        size="xs"
      >
        <Stack gap="lg">
          {/* Navigation Links */}
          <Stack gap={8} pt="md">
            <MenuNavItem
              href="/videos"
              icon={IconVideo}
              label="Free Videos"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/dictionary"
              icon={IconBook2}
              label="Dictionary"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/posts"
              icon={IconNews}
              label="Posts"
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="#"
              icon={IconInfoCircle}
              label="About"
              disabled={true}
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="#"
              icon={IconCurrencyDollar}
              label="Pricing"
              disabled={true}
              onClick={() => setMobileMenuOpen(false)}
            />
            <MenuNavItem
              href="/faq"
              icon={IconHelpCircle}
              label="FAQ"
              onClick={() => setMobileMenuOpen(false)}
            />
          </Stack>

          <Divider color="rgba(255,255,255,0.1)" />

          {/* Action Buttons */}
          <Stack gap="md">
            <Button
              component={Link}
              href="/login"
              variant="light"
              c="#FF9800"
              fullWidth
              styles={{
                root: {
                  fontWeight: 600,
                  transition: "all 0.2s ease",
                },
              }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Login
            </Button>
            <Button
              disabled
              variant="filled"
              fullWidth
              title="Sign up opens at 2.0 launch"
              styles={{
                root: {
                  background: "rgba(255, 152, 0, 0.15)",
                  border: "1px solid rgba(255, 152, 0, 0.25)",
                  color: "rgba(255, 152, 0, 0.6)",
                  fontWeight: 600,
                  cursor: "not-allowed",
                },
              }}
            >
              Sign Up · Coming soon
            </Button>
          </Stack>
        </Stack>
      </NavigationDrawer>

      {/* Main */}
      <AppShell.Main>
        <Box
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            paddingBottom: "clamp(40px, 8vw, 80px)", // Extra space to prevent footer overlay
          }}
        >
          {hideOnScroll ? (
            <HeaderVisibilityProvider headerVisible={headerVisible}>
              {children}
            </HeaderVisibilityProvider>
          ) : (
            children
          )}
        </Box>
      </AppShell.Main>

      <AppShell.Footer p={0}>
        <Footer />
      </AppShell.Footer>
    </AppShell>
  );
}
